import { z } from "zod";
import { stepupFrequencySchema } from "./transaction/transaction";
import { ClientHoldingPatternSchema } from "./client";

export const portfolioSourceSchema = z.enum([
  "INTERNAL",
  "EXTERNAL",
  "OVERALL",
]);

export const productTypeSchema = z.enum(["MF", "GIFT", "SIF"]);

export const productTypeVariantSchema = z.enum([
  "color",
  "grey",
  "slate",
  "accent",
]);

export const portfolioSummarySchema = z.object({
  overall_flag: z.boolean(),
  invested_value: z.string(),
  current_value: z.string(),
  profit_loss: z.string(),
  xirr: z.string(),
  active_xirr: z.string(),
  lifetime_xirr: z.string(),
  absolute_return_pct: z.string(),
  previous_day_return_pct: z.string(),
  previous_day_value: z.string(),
  previous_day_change: z.string(),
  equity_value: z.string(),
  debt_value: z.string(),
  hybrid_value: z.string(),
  equity_pct: z.string(),
  debt_pct: z.string(),
  hybrid_pct: z.string(),
  commodity_pct: z.string(),
  commodity_value: z.string(),
  pan: z.string(),
  calculated_at: z.string(),
  updated_at: z.string(),
  created_at: z.string(),
  source_type: portfolioSourceSchema,
});

export const schemeWiseHoldingSchema = z.object({
  amc_morningstar_code: z.string().nullable(),
  amc_id: z.number().nullable(),
  absolute_return_pct: z.string().nullable(),
  amount: z.string().optional(),
  created_at: z.string(),
  current_value: z.string(),
  gain: z.number().nullable(),
  invested_value: z.string(),
  nav: z.string(),
  redemption_allowed: z.boolean(),
  redemption_max_amount: z.string().nullable(),
  redemption_max_units: z.string().nullable(),
  redemption_min_amount: z.string().nullable(),
  redemption_min_units: z.string().nullable(),
  scheme_isin: z.string(),
  scheme_name: z.string(),
  sip_allowed: z.boolean(),
  stp_allowed: z.boolean(),
  switch_allowed: z.boolean(),
  swp_allowed: z.boolean(),
  units: z.string().optional(),
  updated_at: z.string(),
  internal_flag: z.boolean(),
  xirr: z.string().nullable(),
  lifetime_xirr: z.string().nullable().optional(),
  active_xirr: z.string().nullable().optional(),
  previous_day_change: z.number().nullable().optional(),
  previous_day_return_pct: z.number().nullable().optional(),
});

export const planStatus = z.enum([
  "ACTIVE",
  "CANCELLED",
  "PAUSED",
  "COMPLETED",
]);

export const sipPlanSchema = z.object({
  id: z.number(),
  mandate_id: z.string(),
  fund_scheme_id: z.number().nullable().optional(),
  scheme_id: z.number(),
  scheme_isin: z.string(),
  investment_account_id: z.string(),
  folio_id: z.number(),
  installment_amount: z.string(),
  no_of_installments_paid: z.number(),
  total_installment_amount_paid: z.number(),
  number_of_installments: z.number(),
  installment_due_date: z.string().nullable(),
  prev_paid_date: z.string().nullable(),
  start_date: z.string(),
  end_date: z.string(),
  frequency: z.string(),
  first_order_today: z.boolean(),
  user_remarks: z.string(),
  scheme_name: z.string(),
  status: planStatus,
  amc_morningstar_code: z.string(),
  resume_date: z.string().nullable(),
  step_up_frequency: stepupFrequencySchema.optional(),
  step_up_amount: z.number().nullable().optional(),
  step_up_percentage: z.number().nullable().optional(),
  previous_step_up_date: z.string().nullable().optional(),
  next_step_up_date: z.string().nullable().optional(),
});

export const submittedOrderTypeSchema = z.enum([
  "PURCHASE",
  "SIP INSTALLMENT",
  "REDEMPTION",
  "SWITCH_OUT",
  "SWITCH_IN",
  "SWP INSTALLMENT",
  "STP INSTALLMENT",
]);

export const orderStatusSchema = z.enum([
  "Failed",
  "Payment pending",
  "Payment completed",
  "Under processing",
  "Allotment done",
  "Order completed",
]);

export const submittedOrderGiftSchema = z.object({
  amount: z.string().nullable(),
  fx_rate: z.string().nullable(),
  currency: z.string(),
  glomo_order_id: z.string().nullable(),
  glomo_status: z.string().nullable(),
  target_amount: z.number().nullable(),
  transaction_fee: z.string().nullable(),
  nav: z.string().nullable(),
});

export const submittedOrderSchema = z.object({
  id: z.number(),
  scheme_id: z.string(),
  scheme_name: z.string(),
  scheme_isin: z.string(),
  investment_account_id: z.string(),
  order_remark: z.string(),
  order_status: orderStatusSchema,
  folio_id: z.number(),
  folio_number: z.string(),
  amount: z.string(),
  units: z.string(),
  client_name: z.string().nullable(),
  client_id: z.number().nullable(),
  holding_pattern: ClientHoldingPatternSchema,
  phone_number: z.string().nullable(),
  user_remarks: z.string(),
  created_at: z.string(),
  amc_morningstar_code: z.string(),
  trxn_date: z.string(),
  type: submittedOrderTypeSchema,
  nav: z.number(),
  product_type: productTypeSchema,
  nav_date: z.string().nullable(),
  processing_date: z.string().nullable(),
  gift: submittedOrderGiftSchema.nullable(),
  fund_id: z.string().nullable(),
});

export const stpPlanSchema = z.object({
  id: z.number(),
  switch_out_scheme_id: z.string(),
  switch_in_scheme_id: z.string(),
  folio_id: z.string(),
  installment_amount: z.string(),
  installment_units: z.string(),
  installment_due_date: z.string(),
  number_of_transfers: z.number(),
  no_of_transfers_completed: z.number(),
  total_transfer_amount_completed: z.number(),
  start_date: z.string(),
  first_order_today: z.boolean(),
  user_remarks: z.string(),
  nse_reg_status: z.string(),
  nse_reg_remark: z.string(),
  amc_morningstar_code: z.string(),
  switch_out_scheme_name: z.string(),
  switch_in_scheme_name: z.string(),
  frequency: z.string(),
  status: planStatus,
});

export const swpPlanSchema = z.object({
  id: z.number(),
  scheme_id: z.string(),
  folio_id: z.string(),
  number_of_withdrawals: z.number(),
  start_date: z.string(),
  first_order_today: z.boolean(),
  user_remarks: z.string(),
  nse_reg_status: z.string(),
  status: planStatus,
  installment_amount: z.string(),
  installment_units: z.string(),
  installment_due_date: z.string(),
  nse_reg_remark: z.string(),
  amc_morningstar_code: z.string(),
  scheme_name: z.string(),
  no_of_withdrawals_completed: z.number(),
  total_withdrawal_amount_completed: z.number(),
  frequency: z.string(),
});

export const folioWiseHoldingSchema = z.object({
  folio_number: z.string(),
  folio_id: z.number(),
  scheme_isin: z.string(),
  scheme_name: z.string(),
  units: z.string(),
  amount: z.string(),
  nav: z.string(),
  purchase_allowed: z.boolean(),
  redemption_allowed: z.boolean(),
  switch_allowed: z.boolean(),
  stp_allowed: z.boolean(),
  swp_allowed: z.boolean(),
  sip_allowed: z.boolean(),
  amc_morningstar_code: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  internal_flag: z.boolean(),
  stcg: z.string().nullable().optional(),
  stcg_units: z.string().nullable().optional(),
  ltcg: z.string().nullable().optional(),
  ltcg_units: z.string().nullable().optional(),
  product_type: productTypeSchema,
});
